import { useState, useEffect, useCallback } from 'react';
import { APP_NAME } from '@/lib/brand';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GraduationCap, Plus, Trash2, Save, Loader2, Play, ExternalLink, Eye } from 'lucide-react';

const BIZ_TYPES = [
  { key: 'all', label: 'Todos' },
  { key: 'agency', label: 'Agencias' },
  { key: 'independent', label: 'Independientes' },
  { key: 'developer', label: 'Desarrolladores' },
  { key: 'franchise', label: 'Franquiciatarios' },
];

export default function AcademyPage({ isSuperAdmin = false }) {
  const [sections, setSections] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null);
  const [videoDialog, setVideoDialog] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', visible_for: 'all', visible_for_types: ['all'], visible_for_plan_ids: [], videos: [] });
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const loadSections = useCallback(async () => {
    try {
      const endpoint = isSuperAdmin ? '/admin/academy' : '/academy';
      const r = await api.get(endpoint);
      setSections(r.data || []);
    } catch { /* ok */ }
    finally { setLoading(false); }
  }, [isSuperAdmin]);

  useEffect(() => { loadSections(); }, [loadSections]);
  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get('/admin/dynamic-plans').then(r => setPlans(r.data || [])).catch(() => {});
  }, [isSuperAdmin]);

  const saveSection = async () => {
    if (!form.title.trim()) { toast.error('Titulo requerido'); return; }
    setSaving(true);
    try {
      if (editDialog === 'new') {
        await api.post('/admin/academy', form);
        toast.success('Seccion creada');
      } else {
        await api.put(`/admin/academy/${editDialog}`, form);
        toast.success('Seccion actualizada');
      }
      setEditDialog(null);
      loadSections();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const deleteSection = async (id) => {
    if (!window.confirm('Eliminar esta seccion?')) return;
    try { await api.delete(`/admin/academy/${id}`); toast.success('Eliminada'); loadSections(); }
    catch { toast.error('Error'); }
  };

  const addVideo = () => {
    setForm({ ...form, videos: [...form.videos, { title: '', youtube_url: '', description: '' }] });
  };

  const updateVideo = (idx, field, value) => {
    const videos = [...form.videos];
    videos[idx] = { ...videos[idx], [field]: value };
    setForm({ ...form, videos });
  };

  const removeVideo = (idx) => {
    setForm({ ...form, videos: form.videos.filter((_, i) => i !== idx) });
  };

  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
    return match ? match[1] : null;
  };

  const filtered = filterType === 'all'
    ? sections
    : sections.filter(s => (s.visible_for_types || [s.visible_for || 'all']).includes('all') || (s.visible_for_types || []).includes(filterType) || s.visible_for === filterType);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="academy-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-[#A5D62C]" /> {APP_NAME} Academy
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            {isSuperAdmin ? 'Administra tutoriales y comunicados para los licenciatarios.' : 'Tutoriales y recursos de la plataforma.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isSuperAdmin && (
            <div className="flex gap-1 p-0.5 rounded-lg bg-white/[0.03] border border-white/5">
              {BIZ_TYPES.map(bt => (
                <button key={bt.key} onClick={() => setFilterType(bt.key)} className={`px-3 py-1 rounded-md text-[10px] transition-colors ${filterType === bt.key ? 'bg-[#A5D62C]/10 text-[#A5D62C]' : 'text-white/40'}`}>
                  {bt.label}
                </button>
              ))}
            </div>
          )}
          {isSuperAdmin && (
            <Button onClick={() => { setForm({ title: '', description: '', visible_for: 'all', visible_for_types: ['all'], visible_for_plan_ids: [], videos: [] }); setEditDialog('new'); }} className="bg-[#A5D62C] text-black hover:bg-[#95C225] text-xs gap-1" data-testid="new-academy-section">
              <Plus className="w-3.5 h-3.5" /> Nueva Seccion
            </Button>
          )}
        </div>
      </div>

      {/* Sections */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-xl">
          <GraduationCap className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No hay secciones disponibles</p>
        </div>
      ) : filtered.map(section => (
        <Card key={section.id} className="bg-card/50 backdrop-blur-md border-white/5" data-testid={`academy-section-${section.id}`}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-white">{section.title}</h3>
                {section.description && <p className="text-[10px] text-muted-foreground mt-0.5">{section.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                  <Badge className="text-[8px] bg-white/5 border-white/10">
                  <Eye className="w-2.5 h-2.5 mr-0.5" />
                  {(section.visible_for_types || [section.visible_for || 'all']).map(key => BIZ_TYPES.find(b => b.key === key)?.label || key).join(', ')}
                </Badge>
                {isSuperAdmin && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => { setForm(JSON.parse(JSON.stringify(section))); setEditDialog(section.id); }} className="text-[10px] h-6 border-white/10">Editar</Button>
                    <Button variant="outline" size="sm" onClick={() => deleteSection(section.id)} className="text-[10px] h-6 border-red-500/20 text-red-400"><Trash2 className="w-3 h-3" /></Button>
                  </>
                )}
              </div>
            </div>
            {/* Video Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(section.videos || []).map((video, i) => {
                const ytId = getYouTubeId(video.youtube_url);
                return (
                  <button key={i} type="button" onClick={() => setVideoDialog({ ...video, ytId })} className="group rounded-xl overflow-hidden border border-white/5 hover:border-[#A5D62C]/20 transition-all bg-black/20 text-left" data-testid={`academy-video-${i}`}>
                    {ytId ? (
                      <div className="relative aspect-video bg-black">
                        <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt={video.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Play className="w-10 h-10 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video bg-white/[0.02] flex items-center justify-center">
                        <ExternalLink className="w-6 h-6 text-muted-foreground/30" />
                      </div>
                    )}
                    <div className="p-2.5">
                      <p className="text-xs font-medium text-white truncate">{video.title || 'Sin titulo'}</p>
                      {video.description && <p className="text-[9px] text-muted-foreground mt-0.5 line-clamp-2">{video.description}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
            {!!section.visible_for_plan_ids?.length && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`academy-section-plans-${section.id}`}>
                {section.visible_for_plan_ids.map(planId => {
                  const plan = plans.find(p => p.id === planId);
                  return <Badge key={planId} className="text-[8px] bg-sky-500/10 text-sky-300 border-sky-500/20">{plan?.name || planId}</Badge>;
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      <Dialog open={!!videoDialog} onOpenChange={v => { if (!v) setVideoDialog(null); }}>
        <DialogContent className="max-w-3xl bg-[#0a0c12] border-white/[0.06]" data-testid="academy-video-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">{videoDialog?.title || 'Tutorial'}</DialogTitle>
          </DialogHeader>
          {videoDialog?.ytId ? (
            <div className="space-y-3">
              <div className="aspect-video rounded-xl overflow-hidden border border-white/5">
                <iframe title={videoDialog?.title || 'video'} src={`https://www.youtube.com/embed/${videoDialog.ytId}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
              {videoDialog?.description && <p className="text-xs text-muted-foreground">{videoDialog.description}</p>}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Este video no tiene preview embebido. Abre la URL original:</p>
              <a href={videoDialog?.youtube_url} target="_blank" rel="noreferrer" className="text-sky-400 text-xs underline break-all">{videoDialog?.youtube_url}</a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={v => { if (!v) setEditDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-[#0a0c12] border-white/[0.06]" data-testid="academy-editor">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#A5D62C] flex items-center gap-2">
              <GraduationCap className="w-5 h-5" /> {editDialog === 'new' ? 'Nueva Seccion' : 'Editar Seccion'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[10px]">Titulo de la seccion</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Ej: Tutoriales de Configuracion" data-testid="academy-title" />
            </div>
            <div>
              <Label className="text-[10px]">Descripcion</Label>
              <Textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-black/20 border-white/10 text-xs mt-1 h-16" />
            </div>
            <div>
              <Label className="text-[10px]">Visible para</Label>
              <Select value={form.visible_for || 'all'} onValueChange={v => setForm({ ...form, visible_for: v, visible_for_types: v === 'all' ? ['all'] : [v] })}>
                <SelectTrigger className="bg-black/20 border-white/10 text-xs mt-1 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BIZ_TYPES.map(bt => <SelectItem key={bt.key} value={bt.key} className="text-xs">{bt.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px]">Tipos de licencia visibles</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {BIZ_TYPES.map(bt => {
                  const checked = (form.visible_for_types || []).includes(bt.key);
                  return (
                    <label key={bt.key} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border text-[10px] cursor-pointer ${checked ? 'bg-[#A5D62C]/10 border-[#A5D62C]/20 text-[#A5D62C]' : 'bg-black/20 border-white/10 text-white/50'}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const current = form.visible_for_types || [];
                          const next = checked ? current.filter(v => v !== bt.key) : [...current.filter(v => v !== 'all'), bt.key];
                          setForm({ ...form, visible_for_types: next.length ? next : ['all'] });
                        }}
                        className="sr-only"
                      />
                      <span>{bt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            {isSuperAdmin && plans.length > 0 && (
              <div>
                <Label className="text-[10px]">Visible solo para ciertos planes (opcional)</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto pr-1">
                  {plans.map(plan => {
                    const checked = (form.visible_for_plan_ids || []).includes(plan.id);
                    return (
                      <label key={plan.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border text-[10px] cursor-pointer ${checked ? 'bg-sky-500/10 border-sky-500/20 text-sky-300' : 'bg-black/20 border-white/10 text-white/50'}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setForm({ ...form, visible_for_plan_ids: checked ? form.visible_for_plan_ids.filter(id => id !== plan.id) : [...(form.visible_for_plan_ids || []), plan.id] })}
                          className="sr-only"
                        />
                        <span>{plan.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Videos */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[10px]">Videos (YouTube)</Label>
                <Button variant="ghost" size="sm" onClick={addVideo} className="text-[9px] h-6 gap-0.5"><Plus className="w-3 h-3" /> Agregar video</Button>
              </div>
              <div className="space-y-2">
                {(form.videos || []).map((video, i) => (
                  <div key={i} className="rounded-lg border border-white/5 bg-white/[0.02] p-3 space-y-2" data-testid={`academy-video-form-${i}`}>
                    <div className="flex gap-2">
                      <Input value={video.title || ''} onChange={e => updateVideo(i, 'title', e.target.value)} className="bg-black/20 border-white/10 text-[10px] h-7 flex-1" placeholder="Titulo del video" />
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => removeVideo(i)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                    <Input value={video.youtube_url || ''} onChange={e => updateVideo(i, 'youtube_url', e.target.value)} className="bg-black/20 border-white/10 text-[10px] h-7" placeholder="https://www.youtube.com/watch?v=..." />
                    <Input value={video.description || ''} onChange={e => updateVideo(i, 'description', e.target.value)} className="bg-black/20 border-white/10 text-[10px] h-7" placeholder="Descripcion breve (opcional)" />
                    {getYouTubeId(video.youtube_url) && (
                      <img src={`https://img.youtube.com/vi/${getYouTubeId(video.youtube_url)}/mqdefault.jpg`} alt="" className="w-32 h-auto rounded" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={saveSection} disabled={saving} className="w-full bg-[#A5D62C] text-black hover:bg-[#95C225] text-xs gap-1" data-testid="save-academy-section">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar Seccion
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
